import React, { useState } from "react";
import { FaPlay, FaCheckCircle, FaUndo, FaTable } from "react-icons/fa";
import { secToHMS } from "../../utils/time";

// Sheet check (2026-10-20): the old Google Sheets' story times compared with
// the database (scripts/sheet_reconciliation, db/sheet-review-2026-10-20.sql).
// Each item plays both candidate times in the editor's own player; the chosen
// time is saved on performances like any Timeline edit, and the item records
// who checked it. Items for one episode only; the queue is /episode-editor/sheet-check.

export const VERDICTS = {
  human: { label: "Needs a listen", cls: "bg-red-500/15 text-red-300", order: 0 },
  use_sheet: { label: "Sheet looks right", cls: "bg-purple-500/15 text-purple-300", order: 1 },
  likely_both: { label: "Probably both right", cls: "bg-amber-500/15 text-amber-300", order: 2 },
  keep_db: { label: "Database looks right", cls: "bg-sky-500/15 text-sky-300", order: 3 },
  fill: { label: "Database is blank", cls: "bg-green-500/15 text-green-300", order: 4 },
  missing: { label: "Not in the database", cls: "bg-white/10 text-white/60", order: 5 },
};

const RESOLUTIONS = {
  kept_db: "database time kept", used_sheet: "sheet time used", set_by_ear: "time set by ear",
  added: "story added", not_in_video: "not in this video", skipped: "skipped",
};

const btn = "flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all active:scale-95 disabled:opacity-40";

const Chip = ({ label, sec, tone, onPlay, active }) => (
  sec == null
    ? <span className="px-2.5 py-1 rounded-lg border border-white/10 text-[11px] text-white/35">{label}: —</span>
    : (
      <button onClick={() => onPlay(sec)} title={`Play from ${secToHMS(sec)}`}
        className={`flex items-center gap-2 px-2.5 py-1 rounded-lg border text-left ${tone} ${active ? "ring-1 ring-kk-gold" : ""}`}>
        <span className="text-[10px] uppercase tracking-wide opacity-70">{label}</span>
        <span className="font-mono text-sm">{secToHMS(sec)}</span>
        <FaPlay className="text-[9px] opacity-70" />
      </button>
    )
);

function Item({ item, perf, storyTitle, TimeFieldEl, onPlay, onResolve, onUseSheet, readOnly }) {
  const v = VERDICTS[item.verdict] || VERDICTS.human;
  const which = item.kind === "end" ? "story_end" : "story_start";
  const live = perf ? perf[`${which}_sec`] : null;
  const done = item.status !== "open";
  const others = (item.sheet_values || []).filter((x) => x.sec !== item.sheet_sec);
  const sources = (item.sheet_values || []).find((x) => x.sec === item.sheet_sec)?.src || [];
  const [open, setOpen] = useState(!done);

  if (done && !open) {
    return (
      <div className="flex items-center gap-2 flex-wrap text-[11px] text-white/45 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5">
        <FaCheckCircle className="text-green-400/70" />
        <span className="font-krushna text-white/70">{item.title || storyTitle || "(untitled)"}</span>
        <span>· {RESOLUTIONS[item.resolution] || item.resolution}{item.resolved_sec != null ? ` at ${secToHMS(item.resolved_sec)}` : ""}</span>
        <span>· {item.resolved_by || "someone"}</span>
        <button onClick={() => setOpen(true)} className="text-kk-gold/60 hover:text-kk-gold ml-auto">details</button>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border p-3 space-y-2 ${done ? "border-green-500/20 bg-green-500/[0.03]" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="font-krushna text-kk-cream leading-snug">{item.title || storyTitle || "(untitled)"}</div>
          <div className="text-[11px] text-white/45">{item.teller}{item.kind === "end" ? " · end of story" : item.kind === "missing" ? " · whole story" : " · start of story"}</div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${v.cls}`}>{v.label}</span>
      </div>
      <p className="text-[11px] text-white/50">{item.reason}</p>

      <div className="flex items-center gap-2 flex-wrap">
        {item.kind !== "missing" && <Chip label="Database" sec={item.db_sec} tone="border-sky-400/30 text-sky-200" onPlay={onPlay} active={item.suggested_sec != null && item.suggested_sec === item.db_sec} />}
        <Chip label="Sheet" sec={item.sheet_sec} tone="border-purple-400/30 text-purple-200" onPlay={onPlay} active={item.suggested_sec != null && item.suggested_sec === item.sheet_sec} />
        {others.map((o) => <Chip key={o.sec} label="Other sheet" sec={o.sec} tone="border-white/15 text-white/70" onPlay={onPlay} />)}
        {item.suggested_sec != null && <span className="text-[10px] text-kk-gold/70">ringed = suggested</span>}
      </div>
      {sources.length > 0 && <p className="text-[10px] text-white/30">Sheet rows: {sources.join(", ")}</p>}

      {item.kind !== "missing" && perf && (
        <div className="pt-1">
          {TimeFieldEl(perf, which)}
          {live !== item.db_sec && <p className="text-[10px] text-white/35 pl-[70px]">Now saved as {live == null ? "—" : secToHMS(live)} (was {item.db_sec == null ? "blank" : secToHMS(item.db_sec)} when the sheets were compared)</p>}
        </div>
      )}
      {item.kind !== "missing" && !perf && <p className="text-[11px] text-amber-300/70">This story is no longer in the episode's timeline.</p>}
      {item.kind === "missing" && (
        <p className="text-[11px] text-white/40">If you hear this story at the sheet's time, add it with <b>+ story for …</b> under Stories, set its times in the Timeline, then press <b>Added</b>.</p>
      )}

      <div className="flex items-center gap-2 flex-wrap pt-1">
        {done ? (
          <>
            <span className="text-[11px] text-green-300/80 flex items-center gap-1"><FaCheckCircle /> {RESOLUTIONS[item.resolution] || item.resolution} · {item.resolved_by || "someone"}</span>
            <button onClick={() => onResolve(item, null)} disabled={readOnly} className={`${btn} bg-white/5 text-white/60 hover:text-white`}><FaUndo className="text-[9px]" /> Reopen</button>
            <button onClick={() => setOpen(false)} className={`${btn} text-white/40 hover:text-white`}>Hide</button>
          </>
        ) : item.kind === "missing" ? (
          <>
            <button onClick={() => onResolve(item, { resolution: "added" })} disabled={readOnly} className={`${btn} bg-kk-gold/15 text-kk-gold hover:bg-kk-gold/25`}>Added</button>
            <button onClick={() => onResolve(item, { resolution: "not_in_video" })} disabled={readOnly} className={`${btn} bg-white/5 text-white/70 hover:text-white`}>Not in this video</button>
            <button onClick={() => onResolve(item, { resolution: "skipped", status: "skipped" })} disabled={readOnly} className={`${btn} text-white/40 hover:text-white`}>Not sure</button>
          </>
        ) : (
          <>
            {item.db_sec != null && (
              <button onClick={() => onResolve(item, { resolution: "kept_db", sec: live })} disabled={readOnly || !perf}
                className={`${btn} bg-sky-500/15 text-sky-200 hover:bg-sky-500/25`}>Database is right</button>
            )}
            {item.sheet_sec != null && (
              <button onClick={() => onUseSheet(item, perf, which)} disabled={readOnly || !perf}
                className={`${btn} bg-purple-500/15 text-purple-200 hover:bg-purple-500/25`}>Use sheet time</button>
            )}
            <button onClick={() => onResolve(item, { resolution: "set_by_ear", sec: live })} disabled={readOnly || !perf || live == null}
              title="Set the time with Now / ±1s above first"
              className={`${btn} bg-kk-gold/15 text-kk-gold hover:bg-kk-gold/25`}>Time above is right</button>
            <button onClick={() => onResolve(item, { resolution: "skipped", status: "skipped" })} disabled={readOnly}
              className={`${btn} text-white/40 hover:text-white`}>Not sure</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function SheetCheckPanel({ items, performances, stories, onPlay, TimeFieldEl, onResolve, onUseSheet, readOnly }) {
  const [showDone, setShowDone] = useState(false);
  if (!items.length) return null;
  const perfById = Object.fromEntries(performances.map((p) => [p.performance_id, p]));
  const sorted = [...items].sort((a, b) => (VERDICTS[a.verdict]?.order ?? 9) - (VERDICTS[b.verdict]?.order ?? 9)
    || (a.sheet_sec ?? a.db_sec ?? 0) - (b.sheet_sec ?? b.db_sec ?? 0));
  const open = sorted.filter((i) => i.status === "open");
  const done = sorted.filter((i) => i.status !== "open");
  return (
    <section id="sheet-check" className="border border-purple-400/25 rounded-xl bg-purple-500/[0.03] p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-purple-200 flex items-center gap-2"><FaTable /> Sheet check</h2>
        <span className="text-[11px] text-white/45">{open.length} to check · {done.length} done</span>
      </div>
      <p className="text-[11px] text-white/40">
        The old Google Sheets list a different time (or a story) for this episode. Play each time with the chips, then
        choose. "Use sheet time" saves it on the timeline; you can also set the time by ear with <b>Now</b> and ±1s, then press <b>Time above is right</b>.
      </p>
      <div className="space-y-2">
        {open.map((it) => (
          <Item key={it.item_id} item={it} perf={perfById[it.performance_id]} storyTitle={stories[perfById[it.performance_id]?.story_id]?.title_telugu}
            TimeFieldEl={TimeFieldEl} onPlay={onPlay} onResolve={onResolve} onUseSheet={onUseSheet} readOnly={readOnly} />
        ))}
        {open.length === 0 && <p className="text-xs text-green-300/80 flex items-center gap-2"><FaCheckCircle /> Every item for this episode has been checked.</p>}
      </div>
      {done.length > 0 && (
        <div className="space-y-1.5">
          <button onClick={() => setShowDone((s) => !s)} className="text-[11px] text-white/45 hover:text-white">{showDone ? "▾" : "▸"} {done.length} checked</button>
          {showDone && done.map((it) => (
            <Item key={it.item_id} item={it} perf={perfById[it.performance_id]} storyTitle={stories[perfById[it.performance_id]?.story_id]?.title_telugu}
              TimeFieldEl={TimeFieldEl} onPlay={onPlay} onResolve={onResolve} onUseSheet={onUseSheet} readOnly={readOnly} />
          ))}
        </div>
      )}
    </section>
  );
}
